import React from 'react';
import LegalPage from '@/components/LegalPage';

const JurorCode = () => (
  <LegalPage
    kicker="Jury Charter"
    title="Juror Code of Conduct"
    lastUpdated="26 February 2026"
  >
    <p>
      The AdAura Awards exist because a jury of senior, working professionals
      gives generously of their time to read every entry with care. Joining
      that jury is an honour and a responsibility. This Charter sets out the
      standards we ask every juror to uphold.
    </p>

    <p>
      By accepting an invitation, you confirm that you have read and will be
      bound by this Code.
    </p>

    <h2>1. Independence</h2>
    <p>
      You judge on the work alone. You will not be influenced by reputation,
      personal relationships, agency loyalty, or commercial pressure of any
      kind. If you cannot be impartial about an entry, you must recuse
      yourself.
    </p>

    <h2>2. Confidentiality</h2>
    <ul>
      <li>Every entry — including campaign details, results, files and trade-secret information — is shared in confidence.</li>
      <li>You will not copy, share, screenshot, repost or discuss any unpublished entry outside the Jury Chamber.</li>
      <li>You will not use any entry as competitive intelligence for your own organisation.</li>
      <li>You will not reveal the shortlist or winners outside the jury room before the public announcement.</li>
    </ul>

    <h2>3. Conflict of interest</h2>
    <ul>
      <li>The platform automatically excludes work from your current employer and any client you have personally worked on in the last 12 months.</li>
      <li>If a conflict slips through (your former agency, a client you’ve pitched, a family-owned brand) — you must mark <strong>“Recuse — Conflict”</strong> on that entry. Do not score it.</li>
      <li>If you are uncertain whether something constitutes a conflict, recuse anyway. We will redistribute the entry.</li>
    </ul>

    <h2>4. Blind judging</h2>
    <p>
      The Jury Chamber strips entrant, agency and brand identifiers before
      presenting work to you. You will not seek out or attempt to identify the
      author of an entry. If an entry inadvertently reveals identity, you will
      ignore that information and still mark it on merit.
    </p>

    <h2>5. The scoring rubric</h2>
    <p>
      Every entry is scored across five axes from 1 to 10:
    </p>
    <ul>
      <li><strong>Strategy</strong> — clarity of insight, hypothesis, and target.</li>
      <li><strong>Creativity</strong> — originality of idea, craft, and storytelling.</li>
      <li><strong>Execution</strong> — production quality, channel fluency, attention to detail.</li>
      <li><strong>Impact</strong> — measurable business or behavioural outcomes.</li>
      <li><strong>Innovation</strong> — newness — in technique, format, channel, or category code-breaking.</li>
    </ul>
    <p>
      Reserve 9 and 10 for genuinely exceptional work. Reserve 1 and 2 for work
      that should not have been entered. Most strong entries will sit between 6
      and 8 on each axis.
    </p>

    <h2>6. Commentary</h2>
    <p>
      We may share anonymised juror commentary with shortlisted nominators
      after the announcement, as a service to the industry. Please write
      comments that are <strong>specific, professional and educative</strong> —
      as you would wish a senior colleague to write about your own work.
    </p>

    <h2>7. Time commitment &amp; deadlines</h2>
    <ul>
      <li>The scoring window for the 2026 edition is open until <strong>30 September 2026, 23:59 IST</strong>.</li>
      <li>You will receive weekly progress reminders from us; please pace yourself rather than batching scoring on the final day.</li>
      <li>If you anticipate being unable to complete your assigned categories, please notify the awards desk as early as possible so we can rebalance.</li>
    </ul>

    <h2>8. Public representation</h2>
    <ul>
      <li>You may publicly state that you are a juror for The AdAura Awards 2026 — on LinkedIn, in interviews, in bio lines — and we encourage you to do so.</li>
      <li>You may not, however, speak on behalf of the Awards, comment publicly on entries, or use the AdAura wordmark/logo in any commercial context without written permission.</li>
    </ul>

    <h2>9. Inducements</h2>
    <p>
      You will not solicit, accept or be offered any inducement — financial,
      social, professional, or otherwise — in exchange for a favourable score.
      Any such approach must be reported to the awards desk immediately. The
      offending entrant will be disqualified.
    </p>

    <h2>10. Reporting concerns</h2>
    <p>
      If you observe anything that troubles you — a possible plagiarism, a
      suspected conflict, a process bug, a peer juror behaving inappropriately
      — please write in confidence to{' '}
      <a href="mailto:awards@marketingmind.co">awards@marketingmind.co</a>{' '}
      with the subject line “Jury concern”. We will investigate within five
      business days.
    </p>

    <h2>11. Honorarium &amp; recognition</h2>
    <p>
      AdAura jurors serve on an honorary basis. We do not pay scoring fees.
      Jurors who complete their assigned categories are listed publicly on the
      Jury page and receive a commemorative AdAura Juror Trophy at the Awards
      Night.
    </p>

    <h2>12. Acceptance</h2>
    <p>
      Acceptance of a jury invitation, and login to the Jury Chamber,
      constitutes acknowledgement and acceptance of this Code in full.
    </p>
  </LegalPage>
);

export default JurorCode;
